import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import ModelCard from '../components/ModelCard.jsx';
import DocumentManager from '../components/DocumentManager.jsx';
import { api } from '../utils/api.js';
import { readLocalJson, writeLocalJson, removeLocal, KEYS } from '../utils/storage.js';
import { friendlyError } from '../utils/errors.js';

export default function Result() {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Resolve the scan from either (a) the route's location.state — meaning
  // the tech just finished scanning and was navigated here — or (b) a prior
  // scan stashed in localStorage. Computed once at mount; re-renders on
  // navigation are handled by useEffect below.
  const [resolved, setResolved] = useState(() => {
    if (state?.result) {
      return { result: state.result, capturedImage: state.capturedImage };
    }
    return readLocalJson(KEYS.lastScan, null);
  });

  // Live doc count from DocumentManager's fetch. Seeded from the scan's
  // existing_doc_count snapshot (so the "already on file" banner can paint
  // instantly) but null until DocumentManager reports in — null vs. 0 keeps
  // "not yet loaded" distinguishable from "confirmed zero docs", so a stale
  // snapshot can never out-live a real deletion.
  const [docCount, setDocCount] = useState(() => resolved?.result?.existing_doc_count ?? null);

  // Set when a field-edit PATCH fails — surfaced as a banner near the top of
  // the page. `field`/`value` are kept so the tech can retry the same edit
  // without re-typing it.
  const [saveError, setSaveError] = useState(null);

  // When a fresh scan arrives via location.state, persist it AND replace any
  // prior resolved value. Skips when state is empty (e.g. arriving via
  // bottom-nav while a scan is already cached) so we don't clobber the cache.
  useEffect(() => {
    if (state?.result) {
      const payload = { result: state.result, capturedImage: state.capturedImage };
      setResolved(payload);
      writeLocalJson(KEYS.lastScan, payload);
    }
  }, [state]);

  function startNewScan() {
    removeLocal(KEYS.lastScan);
    setResolved(null);
    setSaveError(null);
    navigate('/');
  }

  // Tech-edited a field on the data-tag card. We update local state +
  // localStorage immediately (so the UI feels instant even on a flaky
  // truck signal), then PATCH the server. The server response carries the
  // re-resolved portal info if the brand changed, which we merge back in.
  // model_number keys every downstream document/troubleshoot session, so a
  // PATCH that never reaches the server can't be allowed to look saved: on
  // failure we revert to the last known-saved value (rather than keeping
  // the optimistic edit) and surface a retry banner.
  async function handleFieldEdit(field, value) {
    if (!resolved) return;
    const prevPayload = resolved;
    const prevResult = resolved.result;

    // Build the optimistic update. When the brand is edited we also clear
    // brand_matched so the UI stops showing the old canonical name until the
    // server returns the freshly-resolved one.
    const optimistic = { ...prevResult, [field]: value };
    if (field === 'brand') {
      optimistic.brand_matched = null;
    }

    const optimisticPayload = { result: optimistic, capturedImage: resolved.capturedImage };
    setResolved(optimisticPayload);
    setSaveError(null);
    writeLocalJson(KEYS.lastScan, optimisticPayload);

    if (!prevResult.lookup_id) return; // No saved row to patch (e.g. cached scan from before lookups existed).

    try {
      const updated = await api.updateLookup(prevResult.lookup_id, { [field]: value });
      // Merge: local fields keep anything the server doesn't return (confidence,
      // tag_photo_key, etc.); server fields overwrite the rest.
      const merged = { ...optimistic, ...updated };
      const mergedPayload = { result: merged, capturedImage: resolved.capturedImage };
      setResolved(mergedPayload);
      writeLocalJson(KEYS.lastScan, mergedPayload);
    } catch (e) {
      console.warn('Failed to sync edit to server:', e);
      setResolved(prevPayload);
      writeLocalJson(KEYS.lastScan, prevPayload);
      setSaveError({ field, value, message: friendlyError(e, 'save that correction') });
    }
  }

  if (!resolved) {
    return (
      <div className="px-5 py-6 max-w-3xl mx-auto">
        <div className="card text-center">
          <p className="text-ink-300 mb-4">No scan data — start by scanning a tag.</p>
          <button onClick={() => navigate('/')} className="btn-primary">📷 Scan Tag</button>
        </div>
      </div>
    );
  }

  const { result, capturedImage } = resolved;
  const modelNumber = result.model_number;

  // docCount stays authoritative once DocumentManager's fetch resolves;
  // existing_doc_count is only the pre-fetch placeholder (and may be
  // undefined on older/persisted scans).
  const knownDocCount = docCount ?? (result.existing_doc_count ?? 0);

  return (
    <div className="px-5 py-6 space-y-5 max-w-3xl mx-auto pb-24">
      {/* Sticky top bar — model + explicit New Scan affordance.
          The negative -mx-5 + px-5 makes the bar visually full-width while
          the rest of the page keeps its 20px gutters. The sticky container
          is the <main> in Layout (overflow-y-auto), which is why top-0 anchors
          to the visible top of the scroll area on iPad. */}
      <div className="flex items-center justify-between gap-3 sticky top-0 z-10 bg-ink-900/95 backdrop-blur -mx-5 px-5 py-3 border-b border-ink-700">
        <div className="min-w-0 flex-1">
          <div className="text-ink-400 text-[10px] font-mono uppercase tracking-widest leading-tight">
            Active scan
          </div>
          <div className="text-white font-mono text-base font-bold break-all">
            {modelNumber || '—'}
          </div>
        </div>
        <button
          type="button"
          onClick={startNewScan}
          className="shrink-0 inline-flex items-center gap-1 min-h-[44px] px-4 py-2 bg-mra-red text-ink-950 text-sm font-semibold rounded-lg shadow-md shadow-mra-red/20 active:scale-[0.98] transition"
          aria-label="Start a new scan and clear the current result"
        >
          + New Scan
        </button>
      </div>

      {/* No-Data-Tag mode: the model shown is an AI best guess from a photo of
          the whole unit, not a tag read — make the tech verify/correct it. */}
      {result.scan_mode === 'unit' && (
        <div className="card border-marginal/40 bg-marginal/5">
          <p className="text-marginal font-semibold">⚠ Best guess from a unit photo</p>
          <p className="text-ink-300 text-sm mt-1">
            No data tag was read — this model was estimated from a photo of the whole appliance
            {result.confidence ? ` (confidence: ${result.confidence})` : ''}. Check it against the unit and
            correct it below before pulling manuals or troubleshooting. A serial number can't be read from a
            unit photo.
          </p>
        </div>
      )}

      {/* Field-edit save failure — the optimistic edit has already been
          reverted by the time this renders, so the card above is back to
          the last known-saved value; this banner is the only place the
          failed correction still exists (for retry). */}
      {saveError && (
        <div className="card border-fail/40 bg-fail/5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-fail-400 font-medium text-sm">⚠ Correction not saved</p>
              <p className="text-ink-300 text-sm mt-1">{saveError.message}</p>
            </div>
            <button
              type="button"
              onClick={() => setSaveError(null)}
              aria-label="Dismiss"
              className="shrink-0 h-11 w-11 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-white active:bg-ink-800 transition"
            >
              ✕
            </button>
          </div>
          <button
            type="button"
            onClick={() => handleFieldEdit(saveError.field, saveError.value)}
            className="btn-secondary w-full mt-3"
          >
            ↻  Try Again
          </button>
        </div>
      )}

      {/* Photo + extracted info */}
      <div className="grid md:grid-cols-2 gap-5">
        {capturedImage && (
          <div className="card p-2 bg-ink-900">
            <img
              src={capturedImage}
              alt={result.scan_mode === 'unit' ? 'Appliance photo' : 'Captured data tag'}
              className="w-full rounded-xl object-cover aspect-[4/3]"
            />
          </div>
        )}
        <ModelCard data={result} onChange={handleFieldEdit} />
      </div>

      {/* Reassures the tech before they even open Documents below — this model's
          tech sheets are already saved, so there's nothing to hunt down or upload. */}
      {knownDocCount > 0 && (
        <div className="card border-pass/30 bg-pass/5">
          <p className="text-pass font-medium">✓ Tech sheet already on file</p>
          <p className="text-ink-300 text-sm mt-1">
            We've already got the documentation for this model — it's loaded below, and the
            troubleshooting AI will use it.
            {knownDocCount > 1 && ` ${knownDocCount} documents on file.`}
          </p>
        </div>
      )}

      {/* Documentation manager — Phase 2 */}
      <div className="card">
        <DocumentManager
          modelNumber={modelNumber}
          lookupId={result.lookup_id}
          onDocumentsChange={(docs) => setDocCount(docs.length)}
        />
      </div>

      {/* Troubleshoot CTA */}
      <Link
        to="/troubleshoot"
        state={{ result, docCount }}
        className="btn-primary w-full text-lg"
      >
        🔧  Get Troubleshooting Help
        {docCount > 0 && (
          <span className="text-xs font-normal opacity-80 ml-2">
            ({docCount} doc{docCount === 1 ? '' : 's'} loaded)
          </span>
        )}
      </Link>

      {/* Bottom New Scan — same effect as the top button, kept here as a
          natural "I'm done with this job" tap target after scrolling. */}
      <button
        onClick={startNewScan}
        className="btn-secondary w-full"
      >
        ← Done — Scan a New Tag
      </button>
    </div>
  );
}
